<template>
<!-- eslint-disable max-len -->
<span>
  <top-bar v-if="narrative.showNavbar" :show-player="narrative.type === 'leftDecision'" :show-right-player="narrative.type === 'rightDecision'">
    <template :slot="narrative.showMetrics ? 'challenge' : 'default'">
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks
        This is what the following line used to be:
        {{ narrativeNavbarText }}
      -->
      <p v-html="narrativeNavbarText"></p>
    </template>
    <cash-navbar v-if="narrative.showMetrics" slot="cash"></cash-navbar>
    <metrics-navbar v-if="narrative.showMetrics" slot="metrics"></metrics-navbar>
  </top-bar>

  <transition
    :enter-active-class="narrative.type === 'rightDecision' && rightCharacterAnimateEnter ? 'animated slideInRight' : narrative.type === 'leftDecision' && leftCharacterAnimateEnter ? 'animated slideInLeft' : ''"
    :leave-active-class="narrative.type === 'rightDecision' && rightCharacterAnimateLeave ? 'animated slideOutRight' : narrative.type === 'leftDecision' && leftCharacterAnimateLeave ? 'animated slideOutLeft' : ''"
  >
    <player
      v-if="showPlayer"
      :right="narrative.type === 'rightDecision'"
      :full="!narrative.showNavbar"
      :variant-override="
        (decisionMade && narrative.type === 'rightDecision')
          ? narrative.rightExtraVariant
          : (decisionMade ? narrative.leftExtraVariant : '')"
      :tooltip-target="narrative.type === 'leftDecision' ? 'characterLeft' : 'characterRight'"
    ></player>
  </transition>

  <bottom-bar
    v-if="enableBackToIntro && !decisionMade"
    :back="onClickBack"
    decision-style
  >
    <span slot="back">
      <span v-html="(game && game.backButtonDefaultText) ? game.backButtonDefaultText : 'Back'"></span>
    </span>
  </bottom-bar>
  <bottom-bar v-else></bottom-bar>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <choice v-if="!decisionMade && decision"
      class="decision"
      :class="{
        left: narrative.type === 'leftDecision',
        right: narrative.type === 'rightDecision',
        'with-bars': narrative.showNavbar
      }"
      :decision="decision"
      @input="onChoice"
      key="choice"
      :shuffled-choice-order="shuffledIndices"
      :submit-button-text="decision.choiceSelectButtonText"
      :dynamic-height-choices="decision.dynamicHeightChoices"
    ></choice>
    <reason v-else-if="decision"
      class="decision"
      :class="{
        left: narrative.type === 'leftDecision',
        right: narrative.type === 'rightDecision',
        'with-bars': narrative.showNavbar
      }"
      :decision="decision"
      :choice="choice"
      :disable-reasoning-form="decision.disableReasoningForm"
      :prevent-reasoning-input-override="decision.preventReasoningInputOverride"
      :decision-reasoning-minimum-length="decision.reasoningMinimumLength"
      :decision-reasoning-minimum-length-override="decision.reasoningMinimumLengthOverride"
      :decision-reasoning-maximum-length="decision.reasoningMaximumLength"
      :decision-reasoning-maximum-length-override="decision.reasoningMaximumLengthOverride"
      :submit-button-text="decision.submitButtonText"
      :back-button-text="decision.backButtonText"
      v-model="reason"
      @back="onReasonBack"
      @input="onReasonSubmit"
      key="reason"
    ></reason>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import TopBar from '../ui/TopBar';
import MetricsNavbar from '../ui/MetricsNavbar';
import CashNavbar from '../ui/CashNavbar';
import Player from '../ui/Player';
import BottomBar from '../ui/BottomBar';
import Choice from '../ui/Choice';
import Reason from '../ui/Reason';

export default {
  // $emit 'input' on complete
  props: {
    decision: { type: Object }
  },
  components: {
    TopBar,
    MetricsNavbar,
    CashNavbar,
    Player,
    BottomBar,
    Choice,
    Reason
  },
  data() {
    return {
      decisionMade: false,
      choice: undefined,
      selectedChoiceIndex: undefined,
      reason: '',
      savedReason: '',
      /** used for analytics */
      timeOnDecisionReferenceDate: new Date(),
      showPlayer: false
    };
  },
  computed: {
    ...mapGetters([
      'game',
      'skipOutrosEnabled',
      'narrative',
      'tooltip',
      'individualDecision',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'leftCharacterAnimateEnter',
      'leftCharacterAnimateLeave',
      'rightCharacterAnimateEnter',
      'rightCharacterAnimateLeave'
    ]),
    shuffledIndices() {
      if (this.decision && this.decision.choices) {
        return this.shuffle(this.decision.choices.map((_, index) => index));
      }
      return [];
    },
    enableBackToIntro() {
      return (this.decision && this.decision.forceEnableBack)
        || (this.game.enableBack && !(this.decision && this.decision.preventEnableBack));
    },
  },
  methods: {
    ...mapActions([
      'stopSounds',
      'submitIndividualDecision',
      'rewindToTooltipTarget',
      'addNarratives'
    ]),
    shuffle(array) {
      // ***
      // Fisher-Yates JS implementation from: https://stackoverflow.com/a/2450976
      // ***
      let currentIndex = array.length;
      let temporaryValue;
      let randomIndex;
      // While there remain elements to shuffle...
      while (currentIndex !== 0) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }
      return array;
    },
    async show() {
      this.showPlayer = true;
      await Vue.nextTick();
      this.stopSounds();
    },
    async reset() {
      this.decisionMade = false;
      this.choice = undefined;
      this.selectedChoiceIndex = undefined;
      this.reason = '';
      this.savedReason = '';
      this.showPlayer =
        (this.narrative.type === 'leftDecision' && !this.leftCharacterAnimateEnter) ||
        (this.narrative.type === 'rightDecision' && !this.rightCharacterAnimateEnter);
      await Vue.nextTick();
      this.stopSounds();
      if ((this.narrative.type === 'leftDecision' && this.leftCharacterAnimateEnter) ||
        (this.narrative.type === 'rightDecision' && this.rightCharacterAnimateEnter)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.showPlayer = true;
      }
    },
    async hide() {
      this.decisionMade = false;
      this.choice = undefined;
      this.selectedChoiceIndex = undefined;
      this.reason = '';
      this.savedReason = '';
      this.showPlayer =
        (this.narrative.type === 'leftDecision' && !this.leftCharacterAnimateLeave) ||
        (this.narrative.type === 'rightDecision' && !this.rightCharacterAnimateLeave);
      await Vue.nextTick();
      this.stopSounds();
    },
    onClickBack() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      if (!(this.decisionMade)) {
        this.$emit('input', true); // narrative back
      }
    },
    onChoice({ choice, selectedChoiceIndex }) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      this.choice = choice;
      if (selectedChoiceIndex === this.selectedChoiceIndex) {
        this.reason = this.savedReason;
      } else {
        this.reason = '';
        this.savedReason = '';
      }
      this.selectedChoiceIndex = selectedChoiceIndex;
      this.decisionMade = true;
    },
    async onReasonBack(reason) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      await Vue.nextTick();
      this.stopSounds(); // stops any choice description voice line (or any sfx played by Reason if that is added)
      this.savedReason = reason;
      this.decisionMade = false;
      await new Promise(resolve => setTimeout(resolve, 475)); // wait for tooltip exit delay
      await this.rewindToTooltipTarget({ target: 'reasonAndConfirmChoiceBubble', tooltips: this.decision.tooltips || [] });
    },
    async onReasonSubmit(reason) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      const { choice } = this;
      const secondsSpentOnDecision = (Date.now() - this.timeOnDecisionReferenceDate.valueOf()) / 1000;

      if (!(this.skipOutrosEnabled) && choice.narratives && choice.narratives.length > 0) {
        this.addNarratives({ narratives: choice.narratives });
      }

      if (choice.wrong) {
        const { narratives } = this.individualDecision;
        this.addNarratives({ narratives });
      }

      for (let i = 3; i > 0; --i) {
        try {
          await this.submitIndividualDecision({
            choice,
            reason,
            duration: secondsSpentOnDecision,
          });
          await this.hide();
          return this.$emit('input');
        } catch (err) {
          if (this.$router.handleGameError(err)) return;
        }
      }
      this.$router.showGameErrorCritical();
    }
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    }
  },
  created() {
    this.showPlayer =
      (this.narrative.type === 'leftDecision' && !this.leftCharacterAnimateEnter) ||
      (this.narrative.type === 'rightDecision' && !this.rightCharacterAnimateEnter);
  },
  async mounted() {
    this.timeOnDecisionReferenceDate = new Date();
    await this.show();
  }
};
</script>

<style lang="scss" scoped>
.decision {
  top: 60px;
}
.decision.with-bars {
  top: 130px;
}
.decision.left {
  left: 370px;
}
.decision.right {
  right: 370px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeDecision.vue